import { TokenListByAddress } from "./constants/tokens";

/**
 * TokenService - Handles fetching and caching of token lists
 *
 * This service fetches token data from an external source (Symphony Assetlist API) and caches
 * the result for the duration of the session. If the API fetch fails, it falls
 * back to the static token list. Subsequent calls return the cached data without
 * making additional API requests.
 *
 * Also handles static token list management including merging with additional tokens,
 * override logic, and native address changes.
 */
class TokenService {
  constructor() {
    this.cache = null;
    this.fetchPromise = null;
    this.lastFetchTime = null;
    this.isUsingFallback = false;
  }

  /**
   * Gets the Symphony token list API URL for a given chain ID
   * @private
   * @param {number} chainId - The chain ID
   * @returns {string} API URL for the token list
   */
  _getTokenListUrl(chainId) {
    // For now, we'll use the Sei mainnet (1329) token list
    // In the future, this could be expanded to support multiple chains
    return `https://raw.githubusercontent.com/Symphony-Exchange/Symphony-Exchange-Assetlist/main/tokens/${chainId}/tokenList.json`;
  }

  /**
   * Fetches the token list from the Symphony Assetlist API with fallback to static tokens
   * Uses caching to avoid repeated API calls within the same session
   *
   * @param {number} chainId - The chain ID to fetch tokens for
   * @returns {Promise<Object>} Token list object with token addresses as keys
   *
   * @example
   * const tokenService = new TokenService();
   * const tokens = await tokenService.fetchTokens(1329);
   * console.log(tokens); // { "0x0": { id: "...", attributes: {...} }, ... }
   */
  async fetchTokens(chainId) {
    // Return cached data if available
    if (this.cache) {
      // console.log("TokenService: Serving tokens from cache");
      return this.cache;
    }

    // If a fetch is already in progress, wait for it to complete
    if (this.fetchPromise) {
      console.log("TokenService: Fetch in progress, waiting for completion");
      return this.fetchPromise;
    }

    // Start new fetch
    // console.log("TokenService: Fetching tokens from API");
    this.fetchPromise = this._performFetch(chainId);

    try {
      const result = await this.fetchPromise;
      return result;
    } finally {
      // Clear the promise reference once completed (success or failure)
      this.fetchPromise = null;
    }
  }

  /**
   * Gets static token list with merging logic for additional tokens and overrides
   * This is used by the synchronous getTokenList() method
   *
   * @param {number} chainId - The chain ID to get tokens for
   * @param {Object} [additionalTokens={}] - Additional tokens to merge
   * @param {boolean} [overrideDefaultTokens=false] - If true, only use additional tokens
   * @param {string} [nativeAddress] - Native token address (for address updates)
   * @returns {Object} Merged token list object
   *
   * @example
   * const tokens = tokenService.getStaticTokenList(1329, { "0x123": {...} }, false);
   */
  getStaticTokenList(
    chainId,
    additionalTokens = {},
    overrideDefaultTokens = false,
    nativeAddress = null
  ) {
    let baseTokens = {};

    // Get base tokens from static list unless overriding
    if (!overrideDefaultTokens) {
      baseTokens = TokenListByAddress[chainId] || {};
      // Create a deep copy to avoid mutations
      baseTokens = JSON.parse(JSON.stringify(baseTokens));
    }

    // Handle native address changes if specified
    if (nativeAddress && !overrideDefaultTokens) {
      baseTokens = this._updateNativeTokenAddress(baseTokens, nativeAddress);
    }

    // Validate and normalize additional tokens
    const validatedAdditionalTokens =
      this._validateAdditionalTokens(additionalTokens);

    // Merge tokens based on override setting
    const mergedTokens = this._mergeTokenLists(
      baseTokens,
      validatedAdditionalTokens,
      overrideDefaultTokens
    );

    return mergedTokens;
  }

  /**
   * Gets async token list with merging logic for additional tokens and overrides
   * This fetches from API first, then applies the same merging logic as static version
   *
   * @param {number} chainId - The chain ID to get tokens for
   * @param {Object} [additionalTokens={}] - Additional tokens to merge
   * @param {boolean} [overrideDefaultTokens=false] - If true, only use additional tokens
   * @param {string} [nativeAddress] - Native token address (for address updates)
   * @returns {Promise<Object>} Merged token list object with async tokens
   *
   * @example
   * const tokens = await tokenService.getAsyncTokenList(1329, { "0x123": {...} }, false);
   */
  async getAsyncTokenList(
    chainId,
    additionalTokens = {},
    overrideDefaultTokens = false,
    nativeAddress = null
  ) {
    let baseTokens = {};

    // Get base tokens from API/fallback unless overriding
    if (!overrideDefaultTokens) {
      baseTokens = await this.fetchTokens(chainId);
      // Create a deep copy to avoid mutations
      baseTokens = JSON.parse(JSON.stringify(baseTokens));
    }

    // Handle native address changes if specified
    if (nativeAddress && !overrideDefaultTokens) {
      baseTokens = this._updateNativeTokenAddress(baseTokens, nativeAddress);
    }

    // Validate and normalize additional tokens
    const validatedAdditionalTokens =
      this._validateAdditionalTokens(additionalTokens);

    // Merge tokens based on override setting
    // This applies whether we got tokens from API or fallback
    const mergedTokens = this._mergeTokenLists(
      baseTokens,
      validatedAdditionalTokens,
      overrideDefaultTokens
    );

    return mergedTokens;
  }

  /**
   * Internal method to perform the actual API fetch with fallback
   *
   * @private
   * @param {number} chainId - The chain ID to fetch tokens for
   * @returns {Promise<Object>} Token list object
   */
  async _performFetch(chainId) {
    try {
      const url = this._getTokenListUrl(chainId);
      // console.log(`TokenService: Fetching tokens from ${url}`);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const tokenList = await response.json();

      // Validate that we received a valid token list object
      if (!tokenList || typeof tokenList !== "object") {
        throw new Error(`Invalid token list format received from API`);
      }

      // Cache the successful result
      this.cache = tokenList;
      this.lastFetchTime = new Date().toISOString();
      this.isUsingFallback = false;

      // console.log(
      //   `TokenService: Successfully fetched and cached ${
      //     Object.keys(tokenList).length
      //   } tokens from Symphony API`
      // );
      return this.cache;
    } catch (error) {
      console.error(
        "TokenService: API fetch failed, falling back to static tokens:",
        error.message
      );
      return this._fallbackToStaticTokens(chainId);
    }
  }

  /**
   * Falls back to static token list when API fetch fails
   *
   * @private
   * @param {number} chainId - The chain ID to get static tokens for
   * @returns {Object} Static token list object
   */
  _fallbackToStaticTokens(chainId) {
    const staticTokens = TokenListByAddress[chainId];

    if (!staticTokens) {
      console.warn(
        `TokenService: No static tokens found for chainId ${chainId}`
      );
      return {};
    }

    // Cache the fallback result
    this.cache = staticTokens;
    this.lastFetchTime = new Date().toISOString();
    this.isUsingFallback = true;

    console.log(
      "TokenService: Successfully fell back to static tokens and cached them"
    );
    return this.cache;
  }

  /**
   * Merges base tokens with additional tokens based on override setting
   *
   * @private
   * @param {Object} baseTokens - Base token list
   * @param {Object} additionalTokens - Additional tokens to merge
   * @param {boolean} overrideDefaultTokens - If true, only use additional tokens
   * @returns {Object} Merged token list
   */
  _mergeTokenLists(baseTokens, additionalTokens, overrideDefaultTokens) {
    if (overrideDefaultTokens) {
      // Only use additional tokens
      return { ...additionalTokens };
    } else {
      // Merge additional tokens with base tokens (additional tokens take precedence)
      return { ...baseTokens, ...additionalTokens };
    }
  }

  /**
   * Updates native token address in the token list
   *
   * @private
   * @param {Object} tokens - Token list to update
   * @param {string} newNativeAddress - New native token address
   * @returns {Object} Updated token list
   */
  _updateNativeTokenAddress(tokens, newNativeAddress) {
    // Find the current native token (address "0x0")
    const currentNativeAddress = "0x0";
    const nativeToken = tokens[currentNativeAddress];

    if (!nativeToken) {
      return tokens;
    }

    // Create updated tokens object
    const updatedTokens = { ...tokens };

    // Add token at new address
    updatedTokens[newNativeAddress] = {
      ...nativeToken,
      attributes: {
        ...nativeToken.attributes,
        address: newNativeAddress,
      },
    };

    // Remove token from old address if different
    if (newNativeAddress !== currentNativeAddress) {
      delete updatedTokens[currentNativeAddress];
    }

    return updatedTokens;
  }

  /**
   * Validates and normalizes additional tokens
   *
   * @private
   * @param {Object} additionalTokens - Additional tokens to validate
   * @returns {Object} Validated and normalized additional tokens
   */
  _validateAdditionalTokens(additionalTokens) {
    if (!additionalTokens || typeof additionalTokens !== "object") {
      return {};
    }

    const validatedTokens = {};

    for (const [address, token] of Object.entries(additionalTokens)) {
      // Validate token structure
      if (!token || typeof token !== "object" || !token.attributes) {
        console.warn(
          `TokenService: Invalid token structure for address ${address}, skipping`
        );
        continue;
      }

      const { attributes } = token;

      // Validate required attributes
      if (
        !attributes.address ||
        !attributes.name ||
        !attributes.symbol ||
        typeof attributes.decimals !== "number"
      ) {
        console.warn(
          `TokenService: Missing required attributes for token ${address}, skipping`
        );
        continue;
      }

      // Normalize addresses to lowercase
      const normalizedAddress = address.toLowerCase();
      const normalizedToken = {
        ...token,
        attributes: {
          ...attributes,
          address: normalizedAddress,
        },
      };

      validatedTokens[normalizedAddress] = normalizedToken;
    }

    return validatedTokens;
  }

  /**
   * Clears the cached token data
   * Useful for testing or forcing a fresh fetch
   */
  clearCache() {
    console.log("TokenService: Clearing token cache");
    this.cache = null;
    this.lastFetchTime = null;
    this.fetchPromise = null;
    this.isUsingFallback = false;
  }

  /**
   * Returns information about the cache status
   *
   * @returns {Object} Cache status information
   */
  getCacheStatus() {
    return {
      isCached: !!this.cache,
      lastFetchTime: this.lastFetchTime,
      isFetching: !!this.fetchPromise,
      tokenCount: this.cache ? Object.keys(this.cache).length : 0,
      isUsingFallback: this.isUsingFallback,
    };
  }
}

// Export a singleton instance
const tokenService = new TokenService();

export default tokenService;
